/**
 * Fire-once gate for the "Delivery And Return Details Viewed" event.
 *
 * Extracted from the three multiseller emitters (desktop `DeliverySLAContainer`,
 * msite `UserEnhancedPinCodeCard`, ajiogram `PinCodeCard`) so the B2C seller
 * cards — six mount points across two platforms — share one implementation
 * instead of copying the block again. Behaviour is deliberately identical to
 * those blocks: observe at 50%, fire once the SLA options have loaded, and reset
 * the guard when the pincode / size moves on.
 *
 * Two trigger modes:
 *   - 'visibility': IntersectionObserver on the returned ref. For cards that can
 *     sit below the fold. A ref replaces the element-id lookup the multiseller
 *     blocks use, because the B2C card has no stable id and is portalled in some
 *     of its mounts.
 *   - 'open': no observer — the seller block *is* the screen (All Sellers), so
 *     visibility is a tautology and "opened + loaded" is the view.
 *
 * Renders nothing and pushes nothing itself: `payload` is invoked lazily, only
 * at the moment the event will actually fire.
 */
import { useEffect, useRef, useState, MutableRefObject } from 'react';

export const DELIVERY_VIEWED_THRESHOLD = 0.5;

interface UseDeliveryDetailsViewedArgs {
  /** Master gate — flag on, surface in scope, nothing covering the card. */
  enabled: boolean;
  mode?: 'visibility' | 'open';
  /** 'open' mode only: whether the screen is currently open. */
  isOpen?: boolean;
  /**
   * SLA data is loaded and non-empty. Matches multiseller's
   * `!isFetching && slaOptions.length` — an empty or errored response does not
   * fire.
   */
  ready: boolean;
  /** Guard resets when any of these change, allowing a re-fire. */
  resetKeys?: unknown[];
  /** Built only when the event is about to fire. Return null to skip. */
  payload: () => void;
}

export const useDeliveryDetailsViewed = ({
  enabled,
  mode = 'visibility',
  isOpen = false,
  ready,
  resetKeys = [],
  payload
}: UseDeliveryDetailsViewedArgs): {
  ref: MutableRefObject<HTMLDivElement | null>;
} => {
  const ref = useRef<HTMLDivElement | null>(null);
  const firedRef = useRef(false);
  const [inView, setInView] = useState(false);
  // Read through a ref so a new closure each render can't re-trigger the fire
  // effect — the multiseller blocks list the payload inputs as deps instead, and
  // the guard is what keeps them to one event either way.
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  /**
   * One scalar for the whole key set, so it can be a dependency of the fire
   * effect as well as the reset. Resetting the guard alone is not enough: a size
   * change usually moves nothing the fire effect watches (it stays enabled,
   * visible and ready), so without this the effect never re-runs and the reset
   * goes unused.
   */
  const resetKey = resetKeys.map(key => String(key)).join('|');

  useEffect(() => {
    firedRef.current = false;
  }, [resetKey]);

  useEffect(() => {
    if (mode !== 'visibility') return undefined;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(Boolean(entry?.isIntersecting));
      },
      { root: null, rootMargin: '0px', threshold: DELIVERY_VIEWED_THRESHOLD }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [mode]);

  const visible = mode === 'open' ? isOpen : inView;

  useEffect(() => {
    if (!enabled) return;
    if (!visible) return;
    if (firedRef.current) return;
    if (!ready) return;
    firedRef.current = true;
    payloadRef.current?.();
  }, [enabled, visible, ready, resetKey]);

  return { ref };
};

export default useDeliveryDetailsViewed;
